import React from "react";
import { Link } from "react-router-dom";
import { ArrowRight, BookMarked } from "lucide-react";
import PageShell from "@/components/layout/PageShell";
import NextStep from "@/components/layout/NextStep";
import { Reveal, SectionOverline } from "@/components/landing/Shared";
import AdaptiveLoopHeader from "@/components/adaptive-loop/AdaptiveLoopHeader";
import { LoopDiagram } from "@/components/adaptive-loop/RegulationLoops";

/**
 * FoundationTemplate — shared shell for the six Scientific Foundations pillars.
 *
 * The section is not a claim that Adaptive Physiology has been validated in a
 * lab. It transparently demonstrates the established scientific disciplines
 * that inform and support the methodology.
 *
 * Every pillar page ends with a "Where This Fits" block that ties back to
 * Foundational Principles, the Adaptive Loop™, the Framework, and Applications.
 */
const FoundationTemplate = ({
  pageKey,
  breadcrumbLabel,
  overline,
  headline,
  headlineAccent,
  lead,
  whyItMatters, // { title, paragraphs: [] }
  concepts, // [ { title, body }, ... 4-6 ]
  representative, // [ "Author — Topic", ... ]
  whereItFits, // { principles: [], loopStage: str, frameworkStep: str, applications: [ { label, to } ] }
  nextPillar, // { label, to }
}) => {
  return (
    <PageShell>
      {/* Hero */}
      <section
        data-testid={`foundation-${pageKey}-hero`}
        className="relative pt-24 sm:pt-32 pb-16 sm:pb-20 border-b border-line-soft overflow-hidden forr-canvas"
      >
        <div className="absolute -top-40 left-1/2 -translate-x-1/2 w-[900px] h-[900px] forr-radial-teal opacity-70 pointer-events-none" />
        <div className="relative max-w-[1400px] mx-auto px-6 sm:px-10 lg:px-16">
          <Reveal>
            <nav
              aria-label="Breadcrumb"
              data-testid={`foundation-${pageKey}-breadcrumb`}
              className="flex items-center gap-2 text-[11px] uppercase tracking-[0.28em] forr-text-mute mb-10"
            >
              <Link to="/" className="hover:forr-text-teal-deep transition-colors">Home</Link>
              <span className="forr-text-faint">›</span>
              <Link to="/foundations" className="hover:forr-text-teal-deep transition-colors">Scientific Foundations</Link>
              <span className="forr-text-faint">›</span>
              <span className="forr-text-soft">{breadcrumbLabel}</span>
            </nav>
          </Reveal>

          <div className="grid grid-cols-12 gap-8 lg:gap-16 items-center">
            <div className="col-span-12 lg:col-span-7">
              <Reveal>
                <div className="inline-flex items-center gap-3 mb-8">
                  <span className="forr-hairline" />
                  <span className="forr-overline">{overline}</span>
                </div>
              </Reveal>
              <Reveal delay={0.05}>
                <h1
                  data-testid={`foundation-${pageKey}-headline`}
                  className="font-display forr-text-ink text-[36px] sm:text-5xl md:text-6xl lg:text-[64px] leading-[1.05] font-medium tracking-[-0.03em]"
                >
                  {headline}
                  {headlineAccent && (
                    <> <span className="forr-text-teal-deep">{headlineAccent}</span></>
                  )}
                </h1>
              </Reveal>
              <Reveal delay={0.15}>
                <p className="mt-8 max-w-2xl text-base sm:text-lg font-light forr-text-soft leading-relaxed">
                  {lead}
                </p>
              </Reveal>
            </div>
            <div className="col-span-12 lg:col-span-5 flex justify-center lg:justify-end">
              <Reveal delay={0.1} className="w-full max-w-[350px]">
                <AdaptiveLoopHeader
                  testId={`foundation-${pageKey}-loop`}
                />
              </Reveal>
            </div>
          </div>
        </div>
      </section>

      {/* 01 · Why This Discipline Matters */}
      <section
        data-testid={`foundation-${pageKey}-why`}
        className="relative py-24 sm:py-32 forr-canvas-alt"
      >
        <div className="max-w-[1400px] mx-auto px-6 sm:px-10 lg:px-16">
          <div className="grid grid-cols-12 gap-8">
            <div className="col-span-12 lg:col-span-4">
              <Reveal>
                <SectionOverline
                  number="01"
                  label="Why This Discipline Matters"
                  testId={`foundation-${pageKey}-why-overline`}
                />
              </Reveal>
              <Reveal delay={0.05}>
                <h2 className="mt-8 font-display forr-text-ink text-3xl sm:text-4xl md:text-5xl font-medium tracking-[-0.02em] leading-[1.08]">
                  {whyItMatters.title}
                </h2>
              </Reveal>
            </div>
            <div className="col-span-12 lg:col-span-8 lg:pl-8">
              <div className="space-y-6">
                {whyItMatters.paragraphs.map((p, i) => (
                  <Reveal delay={0.1 + i * 0.05} key={i}>
                    <p className="text-lg font-light forr-text-soft leading-relaxed">{p}</p>
                  </Reveal>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* 02 · Key Concepts */}
      <section
        data-testid={`foundation-${pageKey}-concepts`}
        className="relative py-24 sm:py-32 forr-canvas border-t border-line-soft"
      >
        <div className="max-w-[1400px] mx-auto px-6 sm:px-10 lg:px-16">
          <Reveal>
            <SectionOverline
              number="02"
              label="Key Concepts"
              testId={`foundation-${pageKey}-concepts-overline`}
            />
          </Reveal>
          <Reveal delay={0.05}>
            <h2 className="mt-8 max-w-3xl font-display forr-text-ink text-3xl sm:text-4xl md:text-5xl font-medium tracking-[-0.02em] leading-[1.08]">
              The concepts Adaptive Physiology™ draws from.
            </h2>
          </Reveal>
          <div className="mt-16 grid sm:grid-cols-2 gap-6">
            {concepts.map((c, i) => (
              <Reveal delay={i * 0.06} key={c.title}>
                <div
                  data-testid={`foundation-${pageKey}-concept-${i + 1}`}
                  className="forr-card rounded-[24px] p-8 h-full"
                >
                  <div className="font-display forr-text-teal-deep text-sm font-medium mb-3">
                    {String(i + 1).padStart(2, "0")}
                  </div>
                  <h3 className="font-display forr-text-ink text-xl font-medium tracking-tight mb-3">
                    {c.title}
                  </h3>
                  <p className="text-sm font-light forr-text-soft leading-relaxed">
                    {c.body}
                  </p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* 03 · Representative Research & Thinkers */}
      <section
        data-testid={`foundation-${pageKey}-representative`}
        className="relative py-24 sm:py-32 forr-canvas-alt border-t border-line-soft"
      >
        <div className="max-w-[1400px] mx-auto px-6 sm:px-10 lg:px-16">
          <div className="grid grid-cols-12 gap-8">
            <div className="col-span-12 lg:col-span-4">
              <Reveal>
                <SectionOverline
                  number="03"
                  label="Representative Research & Thinkers"
                  testId={`foundation-${pageKey}-representative-overline`}
                />
              </Reveal>
              <Reveal delay={0.05}>
                <h2 className="mt-8 font-display forr-text-ink text-3xl sm:text-4xl md:text-5xl font-medium tracking-[-0.02em] leading-[1.08]">
                  Standing on established work.
                </h2>
              </Reveal>
              <Reveal delay={0.15}>
                <p className="mt-8 max-w-md text-base font-light forr-text-soft leading-relaxed">
                  A representative — not exhaustive — sample of the researchers,
                  frameworks, and lines of inquiry Adaptive Physiology™ draws
                  from within this discipline.
                </p>
              </Reveal>
            </div>
            <div className="col-span-12 lg:col-span-8 lg:pl-8">
              <div
                className="rounded-[24px] p-8 sm:p-10 forr-surface"
                style={{ border: "1px solid var(--forr-line)" }}
              >
                <ul className="space-y-4">
                  {representative.map((r, i) => (
                    <li
                      key={r}
                      data-testid={`foundation-${pageKey}-reference-${i + 1}`}
                      className="flex items-start gap-4 border-b border-line-soft pb-4 last:border-0 last:pb-0"
                    >
                      <span className="font-display forr-text-teal-deep text-xs mt-1">
                        {String(i + 1).padStart(2, "0")}
                      </span>
                      <span className="text-sm sm:text-base forr-text-ink font-light">
                        {r}
                      </span>
                    </li>
                  ))}
                </ul>
                <p className="mt-8 text-xs italic forr-text-mute leading-relaxed">
                  A cited literature index for the full Scientific Foundations
                  section will be published as{" "}
                  <span className="forr-text-teal-deep">/foundations/literature</span>{" "}
                  in a forthcoming release.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* 04 · Where This Fits */}
      <section
        data-testid={`foundation-${pageKey}-where-it-fits`}
        className="relative py-24 sm:py-32 forr-canvas border-t border-line-soft"
      >
        <div className="max-w-[1400px] mx-auto px-6 sm:px-10 lg:px-16">
          <Reveal>
            <SectionOverline
              number="04"
              label="Where This Fits"
              testId={`foundation-${pageKey}-fits-overline`}
            />
          </Reveal>
          <Reveal delay={0.05}>
            <h2 className="mt-8 max-w-3xl font-display forr-text-ink text-3xl sm:text-4xl md:text-5xl font-medium tracking-[-0.02em] leading-[1.08]">
              How this discipline shows up in Adaptive Physiology™.
            </h2>
          </Reveal>

          <div className="mt-16 grid sm:grid-cols-2 gap-4">
            {/* Foundational Principles */}
            <div
              data-testid={`foundation-${pageKey}-fits-principles`}
              className="rounded-[24px] p-6 sm:p-8 forr-surface"
              style={{ border: "1px solid var(--forr-line)" }}
            >
              <div className="forr-overline mb-4">Foundational Principles</div>
              <ul className="space-y-2">
                {whereItFits.principles.map((p) => (
                  <li key={p} className="flex items-start gap-3 text-sm forr-text-ink font-light">
                    <BookMarked size={14} strokeWidth={1.6} className="forr-text-teal-deep mt-1 shrink-0" />
                    <span>{p}</span>
                  </li>
                ))}
              </ul>
              <Link
                to="/methodology/why"
                className="mt-6 inline-flex items-center gap-2 text-sm forr-text-teal-deep font-medium"
              >
                Read Why Adaptive Physiology
                <ArrowRight size={13} strokeWidth={1.75} />
              </Link>
            </div>

            {/* Adaptive Loop stage */}
            <div
              data-testid={`foundation-${pageKey}-fits-loop`}
              className="rounded-[24px] p-6 sm:p-8 forr-canvas-warm"
              style={{ border: "1px solid var(--forr-line-soft)" }}
            >
              <div className="forr-overline mb-4">Adaptive Loop™ Stage</div>
              <div className="font-display forr-text-ink text-xl font-medium tracking-tight">
                {whereItFits.loopStage}
              </div>
              <div className="mt-6 flex justify-start">
                <div className="w-full max-w-[280px]">
                  <LoopDiagram
                    variant="adaptive"
                    size="compact"
                    showDescriptions={false}
                    testId={`foundation-${pageKey}-fits-loop-viz`}
                  />
                </div>
              </div>
            </div>

            {/* Framework step */}
            <div
              data-testid={`foundation-${pageKey}-fits-framework`}
              className="rounded-[24px] p-6 sm:p-8 forr-surface"
              style={{ border: "1px solid var(--forr-line)" }}
            >
              <div className="forr-overline mb-4">Framework Step</div>
              <div className="font-display forr-text-ink text-xl font-medium tracking-tight">
                {whereItFits.frameworkStep}
              </div>
              <Link
                to="/methodology/framework"
                className="mt-6 inline-flex items-center gap-2 text-sm forr-text-teal-deep font-medium"
              >
                Open the Framework
                <ArrowRight size={13} strokeWidth={1.75} />
              </Link>
            </div>

            {/* Related Applications */}
            <div
              data-testid={`foundation-${pageKey}-fits-applications`}
              className="rounded-[24px] p-6 sm:p-8 forr-canvas-warm"
              style={{ border: "1px solid var(--forr-line-soft)" }}
            >
              <div className="forr-overline mb-4">Relevant Applications</div>
              <ul className="space-y-2">
                {whereItFits.applications.map((a) => (
                  <li key={a.to}>
                    <Link
                      to={a.to}
                      data-testid={`foundation-${pageKey}-application-${a.to.split("/").pop()}`}
                      className="flex items-center gap-2 text-sm forr-text-ink hover:forr-text-teal-deep transition-colors font-light"
                    >
                      <ArrowRight size={13} strokeWidth={1.75} className="forr-text-teal-deep" />
                      {a.label}
                    </Link>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      </section>

      <NextStep
        overline="Continue"
        title={nextPillar.label}
        description="Continue through the Scientific Foundations that inform the methodology."
        to={nextPillar.to}
        cta="Open pillar"
        testId={`foundation-${pageKey}-next`}
      />
    </PageShell>
  );
};

export default FoundationTemplate;
